/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Row,
  Col,
  Drawer,
  message,
  Select,
  Skeleton,
  Typography,
  Empty,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  LoadingOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useBlustreamApiQuery,
  useBlustreamApiMutation,
  customers,
  products,
  toUserMessages,
} from "apiService/blustreamApi";
import BreadcrumbPageLayout from "Components/page-layouts/BreadcrumbPageLayout";
import AttributeValueField from "commons/AttributeValueField";
import { sameAttrValue } from "utility/attributeValueLogic";

import "./productmapping.css";

const { Search } = Input;

const PAGE_SIZE = 100;

// Loop a paged endpoint until last=true. fn :: ({page,size,signal}) => Promise<ApiPagedResponse>
const loadAllPages = async (fn, signal) => {
  const acc = [];
  let page = 0;
  // Hard cap to avoid runaway loops on bad responses.
  while (page < 1000) {
    const resp = await fn({ page, size: PAGE_SIZE, signal });
    const content = resp?.content || [];
    acc.push(...content);
    if (resp?.page?.last || content.length === 0) break;
    page += 1;
  }
  return acc;
};

const ownerDisplayName = (c) =>
  `${c?.first_name || ""} ${c?.last_name || ""}`.trim();

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { roAdmin } = useSelector((state) => state.loginReducer);

  const [form] = Form.useForm();
  const [existingCustomerForm] = Form.useForm();

  // --- Drawers / modals ---
  const [addContactDrawerOpen, setAddContactDrawerOpen] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [addExistingDrawerOpen, setAddExistingDrawerOpen] = useState(false);
  const [addingExisting, setAddingExisting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState(null);

  // --- Selection + paging ---
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);
  const [contactSearchValue, setContactSearchValue] = useState("");
  const [attributeSearchValue, setAttributeSearchValue] = useState("");

  // --- Optimistic local edits + pending status ---
  // updatedAttributes: { [attribute_definition_id]: value }
  const [updatedAttributes, setUpdatedAttributes] = useState({});
  // savedValues: values saved this session but not yet reflected by a server
  // refetch. They override the (now-stale) current_value for display AND act as
  // the new baseline, so a successful save shows the saved value and doesn't
  // read as unsaved. Cleared when fresh attribute data arrives / owner changes.
  const [savedValues, setSavedValues] = useState({});
  // pendingMap: { [key]: 'pending' | 'success' | { state: 'error', err } }
  // success flashes ~1.5s then clears; error persists until row is acted on
  // again (field edit, drawer reopen).
  const [pendingMap, setPendingMap] = useState({});

  const SUCCESS_FLASH_MS = 1500;

  // Local-only optimistic owner rows that haven't yet returned from the server.
  // shape: same as server owner rows, plus _optimisticKey.
  const [optimisticOwners, setOptimisticOwners] = useState([]);
  // Owner ids that should be hidden from the rendered list during in-flight delete.
  const [hiddenOwnerIds, setHiddenOwnerIds] = useState({});
  // Bumps trigger refetches of owner-derived data.
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = useCallback(() => setRefreshKey((n) => n + 1), []);

  const markPending = useCallback((key) => {
    setPendingMap((m) => ({ ...m, [key]: "pending" }));
  }, []);
  const markError = useCallback((key, err) => {
    setPendingMap((m) => ({ ...m, [key]: { state: "error", err } }));
  }, []);
  const clearPending = useCallback((key) => {
    setPendingMap((m) => {
      const n = { ...m };
      delete n[key];
      return n;
    });
  }, []);
  const markSuccess = useCallback(
    (key) => {
      setPendingMap((m) => ({ ...m, [key]: "success" }));
      setTimeout(() => clearPending(key), SUCCESS_FLASH_MS);
    },
    [clearPending]
  );

  // Clear any pending owner-row states keyed by a prefix. Called when the
  // add-new or add-existing drawer reopens (per ch59926 canonical pattern:
  // error indicator persists until the drawer reopens).
  const clearKeysByPrefix = useCallback((prefix) => {
    setPendingMap((m) => {
      const n = { ...m };
      Object.keys(n).forEach((k) => {
        if (typeof k === "string" && k.startsWith(prefix)) delete n[k];
      });
      return n;
    });
    setOptimisticOwners((rows) =>
      rows.filter((r) => !(r?._optimisticKey || "").startsWith(prefix))
    );
  }, []);

  // --- Queries ---

  // Product header.
  const { data: product, loading: productLoading } = useBlustreamApiQuery(
    (signal) => products.get(productId, { signal }),
    [productId]
  );

  // Product owners (all pages).
  const { data: serverOwners, loading: ownersLoading } = useBlustreamApiQuery(
    (signal) =>
      loadAllPages(
        (opts) => products.listOwners(productId, opts),
        signal
      ),
    [productId, refreshKey]
  );

  // Merge server + optimistic owners, then filter via search.
  // All owners (server + optimistic), before the search filter. Source of truth
  // for "are there any owners at all?" — used to enable/disable the search.
  const mergedOwners = useMemo(() => {
    const server = serverOwners || [];
    return [
      ...server.filter((o) => !hiddenOwnerIds[o?.customer_id]),
      ...optimisticOwners.filter(
        (o) =>
          !server.some((s) => s?.customer_id && s.customer_id === o?.customer_id)
      ),
    ];
  }, [serverOwners, optimisticOwners, hiddenOwnerIds]);

  // The rendered list = owners after applying the search filter.
  const ownersList = useMemo(() => {
    if (!contactSearchValue) return mergedOwners;
    const q = contactSearchValue.toLowerCase().trim();
    return mergedOwners.filter((o) =>
      ownerDisplayName(o).toLowerCase().includes(q)
    );
  }, [mergedOwners, contactSearchValue]);

  // Pick the first owner once data lands, if nothing is selected yet.
  useEffect(() => {
    if (selectedOwnerId) return;
    const first = ownersList[0];
    if (first?.customer_id) setSelectedOwnerId(first.customer_id);
  }, [ownersList, selectedOwnerId]);

  // Reset edit buffer + saved overlay when switching owners (prevents a saved
  // value bleeding onto another owner's same attribute during the refetch gap).
  useEffect(() => {
    setUpdatedAttributes({});
    setSavedValues({});
    setAttributeSearchValue("");
  }, [selectedOwnerId]);

  // Resolve product_instance_id for the selected owner + this product.
  const { data: productInstance, loading: instanceLoading } =
    useBlustreamApiQuery(
      async (signal) => {
        if (!selectedOwnerId) return null;
        const all = await loadAllPages(
          (opts) => customers.listProductInstances(selectedOwnerId, opts),
          signal
        );
        const match = all.find(
          (pi) => String(pi?.product_id) === String(productId)
        );
        return match || null;
      },
      [selectedOwnerId, productId, refreshKey]
    );

  // Fetch attributes for the resolved product instance.
  const { data: attributes, loading: attributesLoading } = useBlustreamApiQuery(
    async (signal) => {
      if (!selectedOwnerId || !productInstance?.product_instance_id) return [];
      return customers.listProductInstanceAttributes(
        selectedOwnerId,
        productInstance.product_instance_id,
        { signal }
      );
    },
    [selectedOwnerId, productInstance?.product_instance_id, refreshKey]
  );

  // Saved attribute values keyed by definition id — the baseline the edit
  // buffer is diffed against to detect unsaved changes.
  const originalByAttrId = useMemo(() => {
    const m = {};
    (attributes || []).forEach((a) => {
      m[a.attribute_definition_id] = a.current_value;
    });
    return m;
  }, [attributes]);

  // Fresh server attribute data is authoritative — drop the local saved overlay
  // once it arrives (the query's data reference is stable between renders, so
  // this only fires on an actual refetch, not every render).
  useEffect(() => {
    setSavedValues({});
  }, [attributes]);

  // Effective baseline per attribute: a value saved this session (not yet
  // refetched) wins over the server's current_value. Used for display and for
  // the unsaved-changes diff so a just-saved value reads as the new normal.
  const baselineByAttrId = useMemo(
    () => ({ ...originalByAttrId, ...savedValues }),
    [originalByAttrId, savedValues]
  );

  // True only when the edit buffer actually differs from the effective baseline.
  // Smart: an edit reverted back to the baseline (original or just-saved) value
  // drops out, so the Save button re-disables itself instead of staying "dirty".
  const hasUnsavedChanges = useMemo(
    () =>
      Object.entries(updatedAttributes).some(
        ([attrId, value]) => !sameAttrValue(value, baselineByAttrId[attrId])
      ),
    [updatedAttributes, baselineByAttrId]
  );

  const filteredAttributes = useMemo(() => {
    const list = attributes || [];
    if (!attributeSearchValue) return list;
    const q = attributeSearchValue.toLowerCase().trim();
    return list.filter((a) =>
      (a?.attribute_definition_name || "").toLowerCase().includes(q)
    );
  }, [attributes, attributeSearchValue]);

  const attributesPanelLoading = ownersLoading
    ? false
    : instanceLoading || attributesLoading;

  // --- Add-existing source list (all customers, paged loop) ---
  // Loaded once when the drawer opens.
  const [allCustomers, setAllCustomers] = useState(null);
  const [allCustomersLoading, setAllCustomersLoading] = useState(false);
  const [allCustomersError, setAllCustomersError] = useState(null);
  const [existingFilter, setExistingFilter] = useState("");

  useEffect(() => {
    if (!addExistingDrawerOpen || allCustomers || allCustomersLoading) return;
    const ctrl = new AbortController();
    setAllCustomersLoading(true);
    setAllCustomersError(null);
    loadAllPages(
      (opts) => customers.list({ ...opts, isActive: true }),
      ctrl.signal
    )
      .then((rows) => {
        setAllCustomers(rows);
        setAllCustomersLoading(false);
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setAllCustomersError(e);
        setAllCustomersLoading(false);
      });
    return () => ctrl.abort();
    // Only re-run when the drawer opens/closes. `allCustomers` / `allCustomersLoading`
    // are SET by this effect — including them here made it re-run the instant loading
    // flipped true, whose cleanup aborted the just-started fetch (loading stuck forever).
    // The guard above still reads them via closure to avoid a duplicate fetch.
  }, [addExistingDrawerOpen]);

  const existingOptions = useMemo(() => {
    if (!allCustomers) return [];
    const ownerIds = new Set(
      (serverOwners || []).map((o) => o?.customer_id).filter(Boolean)
    );
    const filtered = allCustomers
      .filter((c) => !ownerIds.has(c?.customer_id))
      .filter((c) => {
        if (!existingFilter) return true;
        const q = existingFilter.toLowerCase().trim();
        return ownerDisplayName(c).toLowerCase().includes(q);
      })
      .map((c) => ({
        value: c?.customer_id,
        label: ownerDisplayName(c) || c?.email_address || c?.customer_id,
      }));
    return filtered;
  }, [allCustomers, serverOwners, existingFilter]);

  // --- Mutations ---

  const [createCustomerRun] = useBlustreamApiMutation((dto, signal) =>
    customers.create(dto, { signal })
  );
  const [createInstanceRun] = useBlustreamApiMutation(
    (customerId, prodId, signal) =>
      customers.createProductInstance(customerId, prodId, { signal })
  );
  const [removeInstanceRun] = useBlustreamApiMutation(
    (customerId, instanceId, signal) =>
      customers.removeProductInstance(customerId, instanceId, { signal })
  );

  // --- Handlers ---

  const onAttributeChange = (attributeDefId, value) => {
    setUpdatedAttributes((m) => ({ ...m, [attributeDefId]: value }));
    // Edits clear any prior error indicator for that field.
    if (pendingMap[attributeDefId]) clearPending(attributeDefId);
  };

  const onSaveAttributes = async () => {
    // Only persist values that actually differ from the effective baseline (the
    // server value, or a value already saved this session) — a field edited then
    // reverted back to the baseline is a no-op and is skipped.
    const dirty = Object.entries(updatedAttributes).filter(
      ([attrId, value]) => !sameAttrValue(value, baselineByAttrId[attrId])
    );
    if (dirty.length === 0) return;
    if (!productInstance?.product_instance_id) return;

    // Apply optimistic update locally — handled by precedence of updatedAttributes
    // over attributes.current_value in render. Mark pending per attribute.
    dirty.forEach(([attrId]) => markPending(attrId));

    // Call the stateless client directly (NOT a shared mutation hook):
    // useBlustreamApiMutation is single-flight — each run() aborts the prior
    // in-flight call sharing that hook, so saving N attributes through one hook
    // aborted all but the last (only one succeeded). Direct calls fire N truly
    // independent requests we can settle per-attribute.
    const results = await Promise.allSettled(
      dirty.map(([attrId, value]) =>
        customers.saveProductInstanceAttribute(
          selectedOwnerId,
          productInstance.product_instance_id,
          attrId,
          value
        )
      )
    );

    const stillDirty = {};
    const justSaved = {};
    results.forEach((r, i) => {
      const [attrId, value] = dirty[i];
      if (r.status === "fulfilled") {
        // Commit the saved value into the local baseline so the field keeps
        // showing what was saved (the server current_value is still stale until
        // the next refetch).
        justSaved[attrId] = value;
        markSuccess(attrId);
      } else {
        // Keep the client's attempted value (not the prior one) so their unsaved
        // work isn't lost — the field stays dirty + flagged so they can retry.
        stillDirty[attrId] = value;
        markError(attrId, r.reason);
      }
    });

    // Successful entries move from the edit buffer into the saved overlay; failed
    // ones stay in the buffer (still dirty + flagged) so the user can retry.
    setSavedValues((prev) => ({ ...prev, ...justSaved }));
    setUpdatedAttributes(stillDirty);

    const failures = results.filter((r) => r.status === "rejected");
    if (failures.length === 0) {
      message.success("Product attributes saved.");
    } else {
      message.error(
        `Failed to save ${failures.length} attribute${failures.length === 1 ? "" : "s"}.`
      );
    }
  };

  const closeAddContactDrawer = () => {
    form.resetFields();
    setAddContactDrawerOpen(false);
  };

  const handleAddNewContact = async (values) => {
    if (addingContact) return;
    const dto = {
      first_name: values.firstName,
      last_name: values.lastName,
      phone_number: values.phoneNumber,
      email_address: values.emailAddress,
    };

    const optimisticKey = `new-${Date.now()}`;
    // Optimistic row appended; we update the customer_id once known.
    const tempRow = {
      customer_id: optimisticKey,
      first_name: dto.first_name,
      last_name: dto.last_name,
      email_address: dto.email_address,
      phone_number: dto.phone_number,
      _optimisticKey: optimisticKey,
    };
    setOptimisticOwners((rows) => [...rows, tempRow]);
    markPending(optimisticKey);
    setAddingContact(true);

    try {
      const created = await createCustomerRun(dto);
      const realId = created?.customer_id;
      if (!realId) throw new Error("Customer create returned no id");

      await createInstanceRun(realId, productId);

      // Swap the optimistic row's id over to the real one so server refetch
      // can dedupe; flash success briefly before the row is folded into
      // serverOwners on the next refetch.
      setOptimisticOwners((rows) =>
        rows.map((r) =>
          r._optimisticKey === optimisticKey
            ? { ...r, customer_id: realId }
            : r
        )
      );
      markSuccess(optimisticKey);
      message.success("Customer added.");
      closeAddContactDrawer();
      bumpRefresh();
    } catch (e) {
      // Keep the optimistic row visible with a red-x; cleared when the user
      // reopens the drawer (per canonical pattern).
      markError(optimisticKey, e);
      message.error(
        toUserMessages(e)[0] || "Failed to add customer."
      );
    } finally {
      setAddingContact(false);
    }
  };

  const closeAddExistingDrawer = () => {
    existingCustomerForm.resetFields();
    setAddExistingDrawerOpen(false);
    setExistingFilter("");
  };

  const handleAddExisting = async (values) => {
    if (addingExisting) return;
    const ids = values?.stages || [];
    if (ids.length === 0) {
      closeAddExistingDrawer();
      return;
    }

    // Optimistically append rows from allCustomers.
    const lookup = new Map(
      (allCustomers || []).map((c) => [c?.customer_id, c])
    );
    const optimisticEntries = ids
      .map((id) => lookup.get(id))
      .filter(Boolean)
      .map((c) => ({
        ...c,
        _optimisticKey: `exist-${c.customer_id}`,
      }));

    setOptimisticOwners((rows) => [...rows, ...optimisticEntries]);
    optimisticEntries.forEach((r) => markPending(r._optimisticKey));

    setAddingExisting(true);
    try {
      // Call the stateless client directly (NOT the createInstanceRun mutation
      // hook): useBlustreamApiMutation is single-flight — each run() aborts the
      // prior in-flight request sharing that hook, so mapping N ids through the
      // one hook aborted all but the last (earlier ids failed with AbortError).
      // Direct calls fire N truly independent requests we can settle per-row.
      const results = await Promise.allSettled(
        ids.map((id) => customers.createProductInstance(id, productId))
      );

      let failed = 0;
      results.forEach((r, i) => {
        const entry = optimisticEntries[i];
        if (!entry) return;
        if (r.status === "fulfilled") {
          markSuccess(entry._optimisticKey);
        } else {
          // Persistent red-x on the failed row; cleared on drawer reopen.
          markError(entry._optimisticKey, r.reason);
          failed += 1;
        }
      });

      if (failed === 0) {
        message.success("Customers added.");
        closeAddExistingDrawer();
      } else if (failed < ids.length) {
        message.warning(`${ids.length - failed} added, ${failed} failed.`);
      } else {
        message.error("Failed to add customers.");
      }

      bumpRefresh();
    } finally {
      setAddingExisting(false);
    }
  };

  const openDeleteModal = (row) => {
    setPendingDeleteRow(row);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setPendingDeleteRow(null);
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    const row = pendingDeleteRow;
    if (!row) return;
    closeDeleteModal();

    // Need the product_instance_id for this owner. If row is the currently
    // selected owner, we already resolved it. Otherwise resolve on the fly.
    let instanceId = null;
    if (
      row.customer_id === selectedOwnerId &&
      productInstance?.product_instance_id
    ) {
      instanceId = productInstance.product_instance_id;
    } else {
      try {
        const all = await loadAllPages(
          (opts) => customers.listProductInstances(row.customer_id, opts),
          undefined
        );
        const match = all.find(
          (pi) => String(pi?.product_id) === String(productId)
        );
        instanceId = match?.product_instance_id || null;
      } catch (e) {
        message.error(toUserMessages(e)[0] || "Failed to remove customer.");
        return;
      }
    }

    if (!instanceId) {
      message.error("Could not locate the product instance to remove.");
      return;
    }

    // Hide row immediately; pending icon would be moot since the row is gone.
    setHiddenOwnerIds((m) => ({ ...m, [row.customer_id]: true }));
    if (selectedOwnerId === row.customer_id) setSelectedOwnerId(null);

    try {
      await removeInstanceRun(row.customer_id, instanceId);
      message.success("Customer removed.");
      bumpRefresh();
    } catch (e) {
      // Restore the row.
      setHiddenOwnerIds((m) => {
        const n = { ...m };
        delete n[row.customer_id];
        return n;
      });
      message.error(toUserMessages(e)[0] || "Failed to remove customer.");
    }
  };

  // --- Render ---

  const selectedOwner = ownersList.find(
    (o) => o?.customer_id === selectedOwnerId
  );
  const selectedOwnerName = selectedOwner ? ownerDisplayName(selectedOwner) : "";

  return (
    <>
      <BreadcrumbPageLayout
        crumbs={[
          { label: "Products", to: "/product" },
          { label: productLoading ? "" : product?.name || "" },
        ]}
      >
        <div className="product-detail-content-wrapper">
          <Row gutter={[20, 20]}>
            <Col span={24} xs={24} sm={24} md={8} lg={7}>
              <div className="product-detail-section product-owners-section">
                <div className="attributes-title-container">
                  <p className="title">Product owners</p>
                  <div className="search-button-container">
                    <Button
                      className="standard-button secondary-orange-button"
                      onClick={() => {
                        clearKeysByPrefix("exist-");
                        setAddExistingDrawerOpen(true);
                      }}
                      disabled={roAdmin}
                    >
                      Add existing
                    </Button>
                    <Button
                      className="standard-button primary-orange-button"
                      onClick={() => {
                        clearKeysByPrefix("new-");
                        setAddContactDrawerOpen(true);
                      }}
                      disabled={roAdmin}
                    >
                      Create new
                    </Button>
                  </div>
                </div>
                <div className="products-contact-and-search-wrapper">
                  <Search
                    className="standard-searchbox products-contact-searchbox"
                    allowClear
                    prefix={
                      <SearchOutlined className="standard-searchbox-prefix" />
                    }
                    placeholder="Search customer"
                    // Disable only when there are no owners at all — NOT when the
                    // current search happens to match none (else a zero-match
                    // query would disable the field and trap the user).
                    disabled={mergedOwners.length === 0}
                    onChange={(event) => {
                      setContactSearchValue(event.target.value || "");
                    }}
                  />

                  <div className="products-contact-container">
                    {ownersLoading ? (
                      [...Array(5).keys()].map((i) => (
                        <div
                          key={`owner-skel-${i}`}
                          className="contacts-product-detail-container"
                        >
                          <div className="product-wrapper">
                            <Skeleton.Avatar active shape="circle" size={25} />
                            <Skeleton.Input
                              className="owner-skeleton-name"
                              active
                            />
                          </div>
                        </div>
                      ))
                    ) : ownersList.length > 0 ? (
                      ownersList.map((item) => {
                        const display = ownerDisplayName(item);
                        const optimisticKey =
                          item?._optimisticKey || item?.customer_id;
                        const status = pendingMap[optimisticKey];
                        const isPending = status === "pending";
                        const isSuccess = status === "success";
                        const isError =
                          status && status.state === "error";
                        return (
                          <div
                            key={`owner-${optimisticKey}`}
                            className="contacts-product-detail-container"
                            onClick={() => {
                              if (isPending) return;
                              setSelectedOwnerId(item?.customer_id);
                            }}
                          >
                            <div className="product-wrapper">
                              <Typography.Paragraph className="product-image">
                                {display?.slice(0, 1)?.toUpperCase()}
                              </Typography.Paragraph>
                              <Typography.Paragraph
                                className={`product-name ${
                                  selectedOwnerId === item?.customer_id
                                    ? "highlight-product"
                                    : ""
                                }`}
                                ellipsis={{ rows: 1 }}
                              >
                                {display}
                              </Typography.Paragraph>
                              {isPending && (
                                <LoadingOutlined
                                  style={{ marginLeft: 8, color: "#3B84D1" }}
                                />
                              )}
                              {isSuccess && (
                                <CheckCircleFilled
                                  style={{ marginLeft: 8, color: "#389E0D" }}
                                />
                              )}
                              {isError && (
                                <Tooltip
                                  title={
                                    toUserMessages(status.err).join(" ") ||
                                    "Failed"
                                  }
                                >
                                  <CloseCircleFilled
                                    style={{
                                      marginLeft: 8,
                                      color: "#D4380D",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </div>

                            {selectedOwnerId === item?.customer_id ? (
                              <div className="highlighted-bar"></div>
                            ) : null}
                          </div>
                        );
                      })
                    ) : (
                      <Empty
                        description="No customers found!"
                        style={{
                          width: "100%",
                          minHeight: "300px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0px",
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Col>
            <Col span={24} xs={24} sm={24} md={16} lg={17}>
              <div className="product-detail-section product-attributes-section">
                {/* Owner-level header: the owner's name + Save. */}
                <div className="attributes-title-container">
                  <p className="title">
                    {selectedOwnerName || "Select a customer"}
                  </p>
                  <div className="search-button-container">
                    <Button
                      className="standard-button tertiary-button"
                      onClick={() => {
                        if (selectedOwner && !selectedOwner._optimisticKey) {
                          openDeleteModal(selectedOwner);
                        }
                      }}
                      disabled={
                        roAdmin ||
                        !selectedOwner ||
                        Boolean(selectedOwner._optimisticKey)
                      }
                    >
                      Unlink
                    </Button>
                    <Button
                      className="standard-button secondary-orange-button"
                      loading={Object.values(pendingMap).some(
                        (v) => v === "pending"
                      )}
                      onClick={() => {
                        if (hasUnsavedChanges) onSaveAttributes();
                      }}
                      disabled={
                        roAdmin ||
                        !productInstance?.product_instance_id ||
                        !hasUnsavedChanges
                      }
                    >
                      Save
                    </Button>
                  </div>
                </div>
                {/* Attributes sub-section: label + search, then the list. */}
                <div className="owner-attributes-subheader">
                  <p className="owner-attributes-subtitle">Attributes</p>
                  <Search
                    className="standard-searchbox"
                    allowClear
                    prefix={
                      <SearchOutlined className="standard-searchbox-prefix" />
                    }
                    value={attributeSearchValue}
                    placeholder="Search attribute"
                    disabled={
                      attributesPanelLoading ||
                      !(attributes && attributes.length > 0)
                    }
                    onChange={(event) => {
                      setAttributeSearchValue(event.target?.value || "");
                    }}
                  />
                </div>
                <div className="product-attributes-scroll">
                  <Form
                    layout="vertical"
                    className="contact-product-attribute-form"
                    requiredMark={false}
                  >
                  {attributesPanelLoading ? (
                    <Row gutter={[20, 0]}>
                      {[...Array(12).keys()].map((i) => (
                        <Col
                          key={`attr-skel-${i}`}
                          span={24}
                          xs={24}
                          sm={24}
                          md={12}
                          lg={12}
                          xl={12}
                          xxl={8}
                        >
                          <div className="attr-skeleton">
                            <Skeleton.Input
                              className="attr-skeleton-label"
                              active
                            />
                            <Skeleton.Input
                              className="attr-skeleton-input"
                              active
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  ) : filteredAttributes.length > 0 ? (
                    <Row gutter={[20, 0]}>
                      {filteredAttributes.map((item) => {
                          const attrId = item?.attribute_definition_id;
                          const status = pendingMap[attrId];
                          const isPending = status === "pending";
                          const isSuccess = status === "success";
                          const isError = status && status.state === "error";
                          const dirtyValue = updatedAttributes[attrId];
                          const valueToShow =
                            dirtyValue !== undefined
                              ? dirtyValue
                              : baselineByAttrId[attrId] ?? "";
                          return (
                            <Col
                              key={`attr-${attrId}`}
                              span={24}
                              xs={24}
                              sm={24}
                              md={12}
                              lg={12}
                              xl={12}
                              xxl={8}
                            >
                              <Form.Item
                                label={
                                  <span>
                                    {item?.attribute_definition_name}
                                    {isPending && (
                                      <LoadingOutlined
                                        style={{
                                          marginLeft: 8,
                                          color: "#3B84D1",
                                        }}
                                      />
                                    )}
                                    {isSuccess && (
                                      <CheckCircleFilled
                                        style={{
                                          marginLeft: 8,
                                          color: "#389E0D",
                                        }}
                                      />
                                    )}
                                    {isError && (
                                      <Tooltip
                                        title={
                                          toUserMessages(status.err).join(
                                            " "
                                          ) || "Failed to save"
                                        }
                                      >
                                        <CloseCircleFilled
                                          style={{
                                            marginLeft: 8,
                                            color: "#D4380D",
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </span>
                                }
                                className="contact-attribute-form-item"
                              >
                                <AttributeValueField
                                  type={item?.attribute_definition_type}
                                  value={valueToShow}
                                  name={item?.attribute_definition_name}
                                  disabled={roAdmin || isPending}
                                  onChange={(v) =>
                                    onAttributeChange(attrId, v)
                                  }
                                />
                              </Form.Item>
                            </Col>
                          );
                        })}
                    </Row>
                  ) : (
                    <Empty
                      description="No attributes found!"
                      style={{
                        width: "100%",
                        minHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  )}
                  </Form>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </BreadcrumbPageLayout>

      {/* Add new contact drawer */}
      <Drawer
        className="addProperty"
        name="dynamic_form_nest_item"
        width={430}
        title="Add New Customer"
        placement="right"
        onClose={closeAddContactDrawer}
        visible={addContactDrawerOpen}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ paddingBottom: 60 }}
          onFinish={handleAddNewContact}
          requiredMark={false}
          className="add-contact-form"
        >
          <Form.Item
            label="First Name *"
            style={{ width: "100%" }}
            name="firstName"
            rules={[{ required: true, message: "This field can not be empty!" }]}
          >
            <Input className="standard-input" placeholder="Enter First Name" />
          </Form.Item>
          <Form.Item
            label="Last Name *"
            style={{ width: "100%" }}
            name="lastName"
            rules={[{ required: true, message: "This field can not be empty!" }]}
          >
            <Input className="standard-input" placeholder="Enter Last Name" />
          </Form.Item>
          <Form.Item
            label="Phone Number *"
            style={{ width: "100%" }}
            name="phoneNumber"
            rules={[{ required: true, message: "This field can not be empty!" }]}
          >
            <Input className="standard-input" placeholder="Enter Phone Number" />
          </Form.Item>
          <Form.Item
            label="Email Address"
            style={{ width: "100%" }}
            name="emailAddress"
          >
            <Input
              className="standard-input"
              placeholder="Enter Email Address"
            />
          </Form.Item>

          <div
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              left: 0,
              width: "auto",
              borderTop: "1px solid #e9e9e9",
              fontSize: "18px",
              padding: "10px 16px",
              background: "#EFF4F9",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <p style={{ fontSize: 14, color: "#6B7A89" }}>
              All these customers will be added in Customer manager as well
            </p>
            <div style={{ display: "flex" }}>
              <Button
                className="standard-button tertiary-button"
                style={{ marginRight: 8, width: "48%" }}
                onClick={closeAddContactDrawer}
                disabled={addingContact}
              >
                Cancel
              </Button>
              <Button
                className="standard-button primary-orange-button"
                type="primary"
                style={{ width: "48%" }}
                htmlType="submit"
                loading={addingContact}
              >
                Add
              </Button>
            </div>
          </div>
        </Form>
      </Drawer>

      {/* Add existing contact drawer */}
      <Drawer
        className="addProperty"
        name="dynamic_form_nest_item"
        width={430}
        title="Add Existing Customer"
        placement="right"
        onClose={closeAddExistingDrawer}
        visible={addExistingDrawerOpen}
        maskClosable={false}
      >
        <Form
          form={existingCustomerForm}
          layout="vertical"
          style={{ paddingBottom: 60 }}
          onFinish={handleAddExisting}
        >
          <Form.Item
            name="stages"
            className="existing-contact-select"
            label={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <strong style={{ fontSize: "16px" }}>
                  Add existing customer
                </strong>
                {allCustomersLoading ? (
                  <LoadingOutlined
                    style={{ marginLeft: "5px", fontSize: "20px" }}
                  />
                ) : null}
              </div>
            }
          >
            <Select
              mode="multiple"
              showArrow
              allowClear
              style={{ width: "100%" }}
              className="add-existing-contact-multi-select"
              placeholder={
                allCustomersError
                  ? "Failed to load customers"
                  : "Select customers"
              }
              size="large"
              filterOption={false}
              onSearch={setExistingFilter}
              options={existingOptions}
              loading={allCustomersLoading}
              disabled={Boolean(allCustomersError)}
            />
          </Form.Item>

          <div
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              left: 0,
              width: "auto",
              borderTop: "1px solid #e9e9e9",
              fontSize: "18px",
              padding: "10px 16px",
              background: "#EFF4F9",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ display: "flex" }}>
              <Button
                className="standard-button tertiary-button"
                style={{ marginRight: 8, width: "48%" }}
                onClick={closeAddExistingDrawer}
                disabled={addingExisting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                style={{ width: "48%" }}
                htmlType="submit"
                className="standard-button primary-orange-button"
                loading={addingExisting}
              >
                Add
              </Button>
            </div>
          </div>
        </Form>
      </Drawer>

      {/* Delete contact-product confirmation modal */}
      <Modal
        centered
        visible={deleteModalOpen}
        closable={false}
        onCancel={closeDeleteModal}
        footer={null}
        className="delete-contacts-product-modal"
      >
        <div className="delete-content-container">
          <ExclamationCircleFilled className="delete-modal-warning-icon" />
          <p className="contacts-product-delete-modal-dark-text">
            {`Unlink ${ownerDisplayName(pendingDeleteRow) || "this customer"} from this product?`}
          </p>
          <p className="contacts-product-delete-modal-light-text">
            This removes them as a product owner and permanently deletes their
            attributes for this product. The customer itself won't be deleted.
          </p>
          <div className="delete-contacts-product-modal-button-wrapper">
            <Button
              className="standard-button destructive-button"
              onClick={handleConfirmDelete}
            >
              Unlink
            </Button>
            <Button
              className="standard-button secondary-orange-button"
              onClick={closeDeleteModal}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
